import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentsService, dealsService } from '../../services/supabaseService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PaymentStatusTimeline from './components/PaymentStatusTimeline';
import TransactionHistory from './components/TransactionHistory';
import AuthorizePaymentModal from './components/AuthorizePaymentModal';

const PaymentScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [payment, setPayment] = useState(null);
  const [deal, setDeal] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthorizeModal, setShowAuthorizeModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const dealId = location?.state?.dealId;
    if (dealId && user?.id) {
      loadPaymentData(dealId);
    }
  }, [location?.state?.dealId, user]);

  const loadPaymentData = async (dealId) => {
    try {
      setLoading(true);
      const [paymentData, dealData] = await Promise.all([
        paymentsService?.getByDealId(dealId),
        dealsService?.getById(dealId)
      ]);
      
      setPayment(paymentData);
      setDeal(dealData);

      if (paymentData?.id) {
        const history = await paymentsService?.getStatusHistory(paymentData?.id);
        setStatusHistory(history || []);
      }
    } catch (error) {
      console.error('Load payment data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    en: {
      payment: 'Payment',
      backToDeal: 'Back to Deal',
      escrowPayment: 'Escrow Payment',
      dealDetails: 'Deal Details',
      itemName: 'Item Name',
      carrierFee: 'Carrier Fee',
      totalAmount: 'Total Amount',
      paymentStatus: 'Payment Status',
      authorizePayment: 'Authorize Payment',
      holdFunds: 'Hold Funds',
      releaseFunds: 'Release Funds',
      refundPayment: 'Refund Payment',
      transactionHistory: 'Transaction History',
      noPayment: 'No payment found for this deal',
      buyer: 'Buyer',
      traveler: 'Traveler',
      paymentId: 'Payment ID',
      dealId: 'Deal ID',
      currency: 'Currency',
      createdAt: 'Created At',
      lastUpdated: 'Last Updated'
    },
    ko: {
      payment: '결제',
      backToDeal: '거래로 돌아가기',
      escrowPayment: '에스크로 결제',
      dealDetails: '거래 세부정보',
      itemName: '상품명',
      carrierFee: '운송비',
      totalAmount: '총 금액',
      paymentStatus: '결제 상태',
      authorizePayment: '결제 승인',
      holdFunds: '자금 보류',
      releaseFunds: '자금 해제',
      refundPayment: '환불',
      transactionHistory: '거래 내역',
      noPayment: '이 거래에 대한 결제를 찾을 수 없습니다',
      buyer: '구매자',
      traveler: '여행자',
      paymentId: '결제 ID',
      dealId: '거래 ID',
      currency: '통화',
      createdAt: '생성일',
      lastUpdated: '마지막 업데이트'
    }
  };

  const t = translations?.[currentLanguage];

  const formatCurrency = (amount) => {
    return currentLanguage === 'ko' 
      ? `₩${amount?.toLocaleString('ko-KR')}`
      : `$${amount?.toLocaleString('en-US')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAuthorizePayment = async (paymentMethodData) => {
    try {
      setActionLoading(true);
      await paymentsService?.updateStatus(payment?.id, 'authorized', 'Payment authorized by buyer');
      await loadPaymentData(deal?.id);
      setShowAuthorizeModal(false);
    } catch (error) {
      console.error('Authorize payment error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleHoldFunds = async () => {
    try {
      setActionLoading(true);
      await paymentsService?.updateStatus(payment?.id, 'held', 'Funds held in escrow');
      await loadPaymentData(deal?.id);
    } catch (error) {
      console.error('Hold funds error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReleaseFunds = async () => {
    try {
      setActionLoading(true);
      await paymentsService?.updateStatus(payment?.id, 'released', 'Funds released to traveler');
      await loadPaymentData(deal?.id);
    } catch (error) {
      console.error('Release funds error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefund = async () => {
    try {
      setActionLoading(true);
      await paymentsService?.updateStatus(payment?.id, 'refunded', 'Payment refunded to buyer');
      await loadPaymentData(deal?.id);
    } catch (error) {
      console.error('Refund payment error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const getActionButton = () => {
    if (!payment) return null;

    const isBuyer = user?.id === payment?.buyerId;

    switch (payment?.paymentStatus) {
      case 'pending':
        return isBuyer ? (
          <Button
            variant="default"
            size="lg"
            iconName="CreditCard"
            iconPosition="left"
            fullWidth
            onClick={() => setShowAuthorizeModal(true)}
            loading={actionLoading}
          >
            {t?.authorizePayment}
          </Button>
        ) : null;
      
      case 'authorized':
        return isBuyer ? (
          <Button
            variant="warning"
            size="lg"
            iconName="Lock"
            iconPosition="left"
            fullWidth
            onClick={handleHoldFunds}
            loading={actionLoading}
          >
            {t?.holdFunds}
          </Button>
        ) : null;
      
      case 'held':
        return isBuyer ? (
          <div className="flex gap-3">
            <Button
              variant="success"
              size="lg"
              iconName="Unlock"
              iconPosition="left"
              fullWidth
              onClick={handleReleaseFunds}
              loading={actionLoading}
            >
              {t?.releaseFunds}
            </Button>
            <Button
              variant="destructive"
              size="lg"
              iconName="RefreshCcw"
              iconPosition="left"
              fullWidth
              onClick={handleRefund}
              loading={actionLoading}
            >
              {t?.refundPayment}
            </Button>
          </div>
        ) : null;
      
      default:
        return null;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-14 lg:pb-0">
          <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground mb-6">{t?.noPayment}</p>
              <Button
                variant="outline"
                onClick={() => navigate('/buyer-dashboard')}
              >
                {t?.backToDeal}
              </Button>
            </div>
          </div>
        </main>
        <BottomTabNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 pb-14 lg:pb-0">
        <div className="max-w-[1920px] mx-auto">
          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 border-b border-border">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 md:gap-4 min-w-0">
                <button
                  onClick={() => navigate('/deal-room-page', { state: { dealId: deal?.id } })}
                  className="flex-shrink-0 p-2 hover:bg-muted rounded-lg transition-base"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div className="min-w-0">
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground truncate">
                    {t?.payment}
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground caption truncate">
                    {t?.escrowPayment}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_400px] xl:grid-cols-[1fr_480px] gap-6 px-4 md:px-6 lg:px-8 py-6">
            <div className="space-y-6">
              <div className="bg-card rounded-xl border border-border p-6">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-6">
                  {t?.dealDetails}
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t?.itemName}</span>
                    <span className="font-medium text-foreground">{deal?.request?.itemName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t?.carrierFee}</span>
                    <span className="font-medium text-foreground">{formatCurrency(deal?.request?.carrierFee)}</span>
                  </div>
                  <div className="flex justify-between pt-4 border-t border-border">
                    <span className="text-lg font-semibold text-foreground">{t?.totalAmount}</span>
                    <span className="text-lg font-bold text-primary">{formatCurrency(payment?.amount)}</span>
                  </div>
                </div>
              </div>

              <PaymentStatusTimeline
                currentStatus={payment?.paymentStatus}
                statusHistory={statusHistory}
                currentLanguage={currentLanguage}
              />

              <TransactionHistory
                payment={payment}
                currentLanguage={currentLanguage}
              />
            </div>

            <div className="space-y-6">
              <div className="bg-card rounded-xl border border-border p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-6">
                  {t?.paymentStatus}
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t?.paymentId}</span>
                    <span className="text-sm font-mono text-foreground truncate max-w-[200px]">
                      {payment?.id?.substring(0, 8)}...
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t?.currency}</span>
                    <span className="font-medium text-foreground">{payment?.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t?.createdAt}</span>
                    <span className="text-sm text-foreground">{formatDate(payment?.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t?.lastUpdated}</span>
                    <span className="text-sm text-foreground">{formatDate(payment?.updatedAt)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                {getActionButton()}
              </div>
            </div>
          </div>
        </div>
      </main>
      <BottomTabNavigation />

      {showAuthorizeModal && (
        <AuthorizePaymentModal
          payment={payment}
          onClose={() => setShowAuthorizeModal(false)}
          onAuthorize={handleAuthorizePayment}
          currentLanguage={currentLanguage}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default PaymentScreen;